import { gql } from "@apollo/client";
import { API } from "api/API";
import axiosInstance from "api/axiosInstance";
import { CORPORATE_SCOPE_USER_TYPE_IDS, USERTYPE } from "constants/constants";
import { getToken } from "utilities/storageUtilities";

const APIInstance = new API();

export const getPaginatedUsers = gql`
    query getPaginatedUsers($limit: Int!, $offset: Int!, $mill_id: Int!) {
        user(
            limit: $limit
            offset: $offset
            where: { _or: [{ mill_id: { _eq: $mill_id } }, { user_type_id: { _in: [${CORPORATE_SCOPE_USER_TYPE_IDS}] } }], _and: [{is_enabled: {_eq: true}}] }
            order_by: { user_id: asc }
        ) {
            user_id
            first_name
            last_name
            email
            employee_number
            media {
                media_id
                media_url
            }
            user_type {
                user_type_id
                name
                default_user_type_translations{ 
                  user_type_id
                    language {
                      language_id
                      name
                      code
                    }
                    translated_name
                  }
            }
            user_machine_centers(where: { is_enabled: { _eq: true }, machine_center: {disabled_at: {_is_null: true}} }) {
                user_machine_center_id
                is_enabled
                machine_center {
                    production_line_id
                    machine_center_id
                    name
                }
            }
            user_clusters(where: {_and: [{ is_enabled: { _eq: true } }, {cluster: {is_enabled: {_eq: true}}}]}) {
                user_cluster_id
                is_enabled
                cluster {
                    cluster_id
                    name
            }
      }
        }
        user_aggregate(  where: { _or: [{ mill_id: { _eq: $mill_id } }, { user_type_id: { _in: [${CORPORATE_SCOPE_USER_TYPE_IDS}] } }] }) {
            aggregate {
                totalCount: count
            }
        }
    }
`;

export const getUserTypes = gql`
query getUserTypes {
        user_type(where: {user_type_id: {_neq: ${USERTYPE.OSORNOADMINISTRATOR}}}) {
            user_type_id
            name
            default_user_type_translations{ 
              user_type_id
                language {
                  language_id
                  name
                  code
                }
                translated_name
              }
        }
    }
`;

export const getCurrentUserData = gql`
    query getCurrentUserData($where: user_bool_exp) {
        user(where: $where) {
            user_id
            first_name
            last_name
            mill {
                mill_id
                name
                division_id
                region_id
                corporate_id
            }
            email
            employee_number
            media {
                media_id
                media_url
            }
            user_type {
                user_type_id
                name
                default_user_type_translations {
                    user_type_id
                    language {
                        language_id
                        name
                        code
                    }
                    translated_name
                }
            }
        }
        mill(where: { email_notification_settings: {} }) {
            mill_id
            email_notification_settings(where: { is_enabled: { _eq: true } }) {
                notification_type_id
                module_type_id
            }
            corporate {
                rca_enabled
            }
        }
    }
`;

/**
 * Profile document for the API Administrator portal (OS-2557). Deliberately NOT getCurrentUserData:
 * that query selects the `mill` relationship and a second `mill` root field, and Hasura validates
 * field permissions statically — so even a null-resolving relationship would force a `mill` select
 * grant for the api_administrator role. Keeping this document mill-free is what lets the role's
 * Hasura surface stay at the eight tables listed in backend/hasura/metadata.
 */
export const getApiAdminProfile = gql`
    query getApiAdminProfile($where: user_bool_exp) {
        user(where: $where) {
            user_id
            first_name
            last_name
            email
            employee_number
            media {
                media_id
                media_url
            }
            user_type {
                user_type_id
                name
                default_user_type_translations {
                    user_type_id
                    language {
                        language_id
                        name
                        code
                    }
                    translated_name
                }
            }
        }
    }
`;

export const upsertUser = gql`
    mutation upsertUser($user: [user_insert_input!]!) {
        insert_user(
            objects: $user
            on_conflict: {
                constraint: user_pkey
                update_columns: [first_name, last_name, employee_number, user_type_id, mill_id]
            }
        ) {
            affected_rows
            returning {
                user_id
            }
        }
    }
`;

export const uploadUserImage = async (
    userId: number,
    userImageFormData: FormData,
): Promise<{ publicUrl: string } | unknown> => {
    try {
        const url = `${APIInstance.api_root}/upload/user-image/${userId}`;
        const response = await axiosInstance.put<{ publicUrl: string }>(url, userImageFormData);

        return response;
    } catch (error) {
        console.error(error);
        return error;
    }
};

export const getUsersByType = gql`
    query getUsersByType($where: user_bool_exp) {
        user(where: $where) {
            user_id
            first_name
            last_name
            email
            employee_number
            avatar_media_id
            media {
                media_id
                media_url
            }
            user_type {
                user_type_id
                name
                default_user_type_translations {
                    user_type_id
                    language {
                        language_id
                        name
                        code
                    }
                    translated_name
                }
            }
        }
    }
`;

export const softDeleteUser = gql`
    mutation softDeleteUser($user_id: Int!) {
        update_user(where: { user_id: { _eq: $user_id } }, _set: { is_enabled: false }) {
            affected_rows
            returning {
                user_id
                is_enabled
            }
        }
    }
`;

export const changeUserPassword = async (values: {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
}): Promise<any> => {
    const currentToken = getToken();

    const url = `${APIInstance.api_root}/user/change-password`;
    const response = await axiosInstance.post(url, values, {
        headers: {
            authorization: `Bearer ${currentToken}`,
            "content-type": "application/json",
        },
    });

    return response;
};

export const forgotPasswordRequest = async (values: { email: string; locale: string }): Promise<any> => {
    const url = `${APIInstance.api_root}/user/forgot-password-request`;
    const response = await axiosInstance.post(url, values);
    return response;
};

export const forgotPasswordReset = async (values: {
    newPassword: string;
    confirmPassword: string;
    token: string;
}): Promise<any> => {
    const url = `${APIInstance.api_root}/user/forgot-password-reset`;
    const response = await axiosInstance.post(url, values);
    return response;
};

export const checkUserPassword = async (values: { password: string }): Promise<any> => {
    const currentToken = getToken();
    const url = `${APIInstance.api_root}/user/check-password`;
    const response = await axiosInstance.post(url, values, {
        headers: {
            authorization: `Bearer ${currentToken}`,
            "content-type": "application/json",
        },
    });

    return response;
};

export const getAllUsers = gql`
    query getAllUsers($where: user_bool_exp) {
        user(where: $where) {
            user_id
            first_name
            last_name
            mill {
                mill_id
                name
                corporate {
                    corporate_id
                    name
                }
                region {
                    region_id
                    name
                }
            }
            user_type {
                user_type_id
                name
            }

            media {
                media_id
                media_url
            }
        }
    }
`;

export const GetUserNotificationSettings = gql`
    query GetUserNotificationSettings($corporateEnabledNotifications: [Int!]!) {
        user_notification_settings(
            where: { notification_type_id: { _in: $corporateEnabledNotifications } }
            order_by: { email_notification_type: { id: asc } }
        ) {
            id
            email_notification_type {
                id
                type
                module_type_id
                default_email_notification_type_translations {
                    notification_type_id
                    language {
                        language_id
                        name
                        code
                    }
                    translated_type
                }
            }
            is_enabled
            user_machine_center_notification_settings(
                where: { machine_center: { email_notification_machine_center_settings: { is_enabled: { _eq: true } } } }
            ) {
                is_enabled
                machine_center {
                    machine_center_id
                    name
                    production_line {
                        production_line_id
                        name
                    }
                }
            }
        }
    }
`;

export interface IGetUserNotificationSettingsQuery {
    user_notification_settings: {
        id: number;
        email_notification_type: {
            id: number;
            type: string;
            module_type_id: number;
            default_email_notification_type_translations: {
                notification_type_id: number;
                language: {
                    language_id: number;
                    name: string;
                    code: string;
                };
                translated_type: string;
            }[];
        };
        is_enabled: boolean;
        user_machine_center_notification_settings: {
            is_enabled: boolean;
            machine_center: {
                machine_center_id: number;
                name: string;
                production_line: {
                    production_line_id: number;
                    name: string;
                };
            };
        }[];
    }[];
}

export const enabledMillNotificationSettings = gql`
    query enabledMillNotificationSettings($userTypeId: Int) {
        email_notification_settings(
            where: {
                is_enabled: { _eq: true }
                email_notification_type: {
                    _or: [
                        {
                            user_type_conditioned: { _eq: true }
                            user_type_notification_types: { user_type_id: { _eq: $userTypeId } }
                        }
                        { user_type_conditioned: { _eq: false } }
                    ]
                }
                _or: [
                    { mill: { corporate: { rca_enabled: { _eq: false } } }, module_type_id: { _neq: 2 } }
                    { mill: { corporate: { rca_enabled: { _eq: true } } } }
                ]
            }
        ) {
            notification_type_id
        }
    }
`;
export interface IEnabledMillNotificationSettingsQuery {
    email_notification_settings: {
        notification_type_id: number;
    }[];
}

export const updateUserNotificationSettings = gql`
    mutation updateUserNotificationSettings($settingId: Int!, $change: Boolean) {
        update_user_notification_settings(where: { id: { _eq: $settingId } }, _set: { is_enabled: $change }) {
            affected_rows
        }
    }
`;

export const updateUserMachineCenterNotificationSettings = gql`
    mutation updateUserMachineCenterNotificationSettings(
        $where: user_machine_center_notification_settings_bool_exp!
        $change: Boolean
    ) {
        update_user_machine_center_notification_settings(where: $where, _set: { is_enabled: $change }) {
            affected_rows
        }
    }
`;

export const UpsertUserFiltersPreferences = gql`
    mutation upsertUserFiltersPreferences($userPreference: user_filter_preferences_insert_input!) {
        insert_user_filter_preferences_one(
            object: $userPreference
            on_conflict: {
                constraint: user_filter_preferences_user_id_report_id_key
                update_columns: [filter_selection]
            }
        ) {
            id
            filter_selection
        }
    }
`;

export const DeleteUserFiltersPreferences = gql`
    mutation deleteUserFiltersPreferences($userId: Int!, $reportId: Int!) {
        delete_user_filter_preferences(where: { user_id: { _eq: $userId }, report_id: { _eq: $reportId } }) {
            affected_rows
            returning {
                id
            }
        }
    }
`;

export const GetUserFiltersPreferences = gql`
    query getUserFiltersPreferences($where: user_filter_preferences_bool_exp!) {
        user_filter_preferences(where: $where) {
            id
            filter_selection
        }
    }
`;
export interface IGetUserFiltersPreferencesQuery {
    user_filter_preferences: {
        id: number;
        filter_selection: string;
    }[];
}
